import { useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { Download, Trash } from 'react-bootstrap-icons';
import { ClientSelectGroup } from '@/components/ClientSelectGroup';
import { configFileFor } from '@/components/config/configFile';
import { ToolPage } from '@/components/ui/ToolPage';
import { clientService } from '@/services/clientService';
import { Client } from '@/types';
import { FileExportService } from '@/utils/fileExport';

export default function DeleteClientConfig() {
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Shown inside the confirm dialog, which covers the page while it is open.
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletedName, setDeletedName] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const list = await clientService.getAllClients();
        setClients(list);
      } catch (e) {
        const detail = e instanceof Error ? ` ${e.message}` : '';
        setError(`Could not load the client list. Reload the page to try again.${detail}`);
      }
    })();
  }, []);

  const handleDeleteClick = () => {
    setError(null);
    setDeleteError(null);
    setDeletedName(null);
    if (!selectedClientName) {
      setError('Choose a client to delete.');
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      setWorking(true);
      setDeleteError(null);
      await clientService.deleteClientConfig(selectedClientName);
      setDeletedName(selectedClientName);
      setSelectedClientName('');
      setShowConfirmModal(false);

      // The deleted client must leave the picker.
      const list = await clientService.getAllClients();
      setClients(list);
    } catch (err) {
      const detail = err instanceof Error ? ` ${err.message}` : '';
      setDeleteError(`The config was not deleted.${detail}`);
    } finally {
      setWorking(false);
    }
  };

  const selectedClient = clients.find((c) => c.name === selectedClientName);

  const downloadCopy = () => {
    if (!selectedClient) return;
    FileExportService.downloadFile(configFileFor(selectedClient.name, selectedClient.config));
  };

  return (
    <ToolPage
      title="Delete client config"
      subtitle="Remove a client's config from the portal. Its AWS account and its workspace stay as they are."
      width="narrow"
    >
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {deletedName && (
        <Alert variant="success" dismissible onClose={() => setDeletedName(null)}>
          Deleted the config for <strong>{deletedName}</strong>. Its AWS account and its workspace were not changed.
        </Alert>
      )}

      <Card>
        <Card.Body>
          {/* Enter in the client search must not send the form. */}
          <Form className="tool-form" onSubmit={(e) => e.preventDefault()}>
            <section className="tool-form__section">
              <h2 className="section-label">Client</h2>
              <ClientSelectGroup value={selectedClientName} onChange={setSelectedClientName} clients={clients} />
            </section>

            {selectedClient && (
              <section className="tool-form__section">
                <h2 className="section-label">What deleting does</h2>
                <dl className="tool-facts mb-3">
                  <dt>Client</dt>
                  <dd>{selectedClient.name}</dd>
                  <dt>AWS account</dt>
                  <dd className="mono">{selectedClient.config.clientAccountId}</dd>
                  <dt>Region</dt>
                  <dd className="mono">{selectedClient.config.region}</dd>
                  <dt>Type</dt>
                  <dd>{selectedClient.config.devInstance ? 'Dev stack' : 'Customer stack'}</dd>
                </dl>
                <div className="tool-effect">
                  <ul>
                    <li>
                      <strong>Removed:</strong> the config for {selectedClient.name}, with its region, apps and feature
                      switches. The client leaves the Configs page and every client picker, so nobody can deploy it or
                      change it from the portal.
                    </li>
                    <li>
                      <strong>Not removed:</strong> the AWS account, the deployed workspace, its people and its data.
                      The workspace keeps running on its last deploy.
                    </li>
                    <li>
                      <strong>No undo:</strong> to bring the client back, create its config again. Download a copy first
                      if you may need it.
                    </li>
                  </ul>
                </div>
              </section>
            )}

            <div className="tool-form__actions">
              <Button variant="outline-danger" onClick={handleDeleteClick} disabled={working || !selectedClientName}>
                <Trash className="me-1" aria-hidden="true" />
                Delete config
              </Button>
              <Button variant="outline-secondary" onClick={downloadCopy} disabled={!selectedClient}>
                <Download className="me-1" aria-hidden="true" />
                Download a copy
              </Button>
              {!selectedClientName && <span className="tool-form__hint">Choose a client first.</span>}
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Modal show={showConfirmModal} onHide={() => !working && setShowConfirmModal(false)} centered>
        <Modal.Header closeButton={!working}>
          <Modal.Title>Delete this config?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This removes the config for <strong>{selectedClientName}</strong> from the portal. The AWS account and the
            deployed workspace stay. You cannot undo it here.
          </p>
          {selectedClient && (
            <dl className="tool-facts">
              <dt>Client</dt>
              <dd>{selectedClient.name}</dd>
              <dt>AWS account</dt>
              <dd className="mono">{selectedClient.config.clientAccountId}</dd>
              <dt>Region</dt>
              <dd className="mono">{selectedClient.config.region}</dd>
            </dl>
          )}
          {deleteError && (
            <Alert variant="danger" className="mt-3 mb-0">
              {deleteError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)} disabled={working}>
            Cancel
          </Button>
          <Button variant="outline-danger" onClick={confirmDelete} disabled={working}>
            {working ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting
              </>
            ) : (
              `Delete config for ${selectedClientName}`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </ToolPage>
  );
}
